<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter, RouterLink } from 'vue-router'
import { dataManager } from '@/dataManager'
import { Bar, Line } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement,
  BarElement, CategoryScale, LinearScale, LineController,
  PointElement, TimeScale, Colors, Filler} from 'chart.js'
import 'chartjs-adapter-luxon'
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(Title, Tooltip, Legend, LineElement,
  BarElement, CategoryScale, LinearScale, LineController,
  PointElement, TimeScale, annotationPlugin, Colors, Filler)

const emit = defineEmits<{
  (e: 'backButton', backUrl: string|null): void
}>()

const router = useRouter()

const dm = dataManager.getInstance()

const numVisibleDatas = ref<number>(0)

const lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    annotation: {
      annotations: calcVerticalLines()
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      max: 100
    },
    x: {
      type: 'time',
      time: {
        unit: 'day'
      }
    }
  }
}

const areasData : {
  title: string,
  numPoints: number,
  chartData: any
}[] = calcAreasData()

onMounted(() => {
  console.log("Statistics mounted")
  emit('backButton', '/')
})

function calcVerticalLines(): any[] {
  let verticalLineAnnotations: any[] = []
  dm.getCurrentUserData().importantDates.forEach((importantDate) => {
    verticalLineAnnotations.push({
          type: 'line',
          mode: 'vertical',
          scaleID: 'x',
          value: importantDate.date.toISOString(),
          borderColor: 'red',
          borderWidth: 2,
          label: {
              display: true,
              content: importantDate.title,
              backgroundColor: 'rgba(0,0,0,0.5)',
              position: 'start',
              font: {
                size: '9pt'
              }
          }
      })
  })
  return verticalLineAnnotations
}

function calcAreasData() {
  let calcNumVisibleDatas = 0
  const initialAreasData : typeof areasData = []
  dm.getMainQuestionnaireList().forEach((questionnaire) => {
    let questionnaireRuns = dm.getAllQuestionnaireRuns(questionnaire.id)
    let runsData: {x: string, y: number}[] = []
    let maxScore = questionnaire.questions.length * 5
    questionnaireRuns.forEach((questionnaireRun) => {
      runsData.push({
        x: questionnaireRun.date.toISOString(),
        y: dm.normalizeToPercentage(questionnaireRun)
      })
    })
    let [mainTitle, subQuestionnaireTitle] = dm.extractTitles(questionnaire.title)
    const initialAreaData = {
      title: mainTitle,
      numPoints: questionnaireRuns.length,
      chartData: {
        datasets: [{
          data: runsData,
          cubicInterpolationMode: 'monotone',
          tension: 0.4,
          borderColor: '#10acc5',
          //backgroundColor: '#c5f4ff',
          backgroundColor: '#10acc533',
          fill: 'origin'
        }]
      }
    }
    initialAreasData.push(initialAreaData)
    if (hasEnoughData(initialAreaData)) {
      calcNumVisibleDatas ++
    }
  })
  numVisibleDatas.value = calcNumVisibleDatas
  return initialAreasData
}

function hasEnoughData(areaData: any): boolean {
  return areaData.numPoints >= 2
}

function allChartData() {
  let labels: string[] = []
  let datasets : any = []
  dm.getMainQuestionnaireList().forEach((questionnaire) => {
    labels.push(questionnaire.title)
    let runs = dm.getAllQuestionnaireRuns(questionnaire.id)
    let runsData: {x: string, y: number}[] = []
    let maxScore = questionnaire.questions.length * 5
    runs.forEach((questionnaireRun) => {
      runsData.push({
        x: questionnaireRun.date.toISOString(),
        y: dm.normalizeToPercentage(questionnaireRun)
      })
    })
    datasets.push({
      label: questionnaire.title,
      data: runsData
    })
  })
  return {
    datasets: datasets
  }
}

function allChartOptions() {
  let clon = JSON.parse(JSON.stringify(lineChartOptions))
  clon.plugins.legend.display = true
  return clon
}

</script>

<template>
  <h2>Tu evolución en las distintas áreas</h2>

  <p>Recuerda que una puntuación elevada indica mayores dificultades. Si en
    algún área <b>la puntuación disminuye</b> a lo largo del tiempo, quiere decir que
    <b>estás mejorando</b>.</p>

  <p v-if="dm.getCurrentUserData().importantDates.length == 0">
    En el apartado <b>fechas señaladas</b>
    puedes anotar el inicio o los cambios en tus tratamientos para valorar tu
    evolución.
  </p>

  <div v-for="areaData in areasData" class="areaData">
  <div v-if="areaData.numPoints >= 2">
    <h3>{{ areaData.title }}</h3>
    <p v-if="areaData.numPoints < 2">No tenemos suficientes datos para mostrarte la evolución todavía. Continúa cubriendo cuestionarios.</p>
    <div v-if="areaData.numPoints >= 2" class="areadata-chart">
      <Line
        class="summary-chart"
        :options="(lineChartOptions as unknown) as undefined"
        :data="areaData.chartData"
        />
    </div>
  </div>
  </div>

  <div class="no-data-messages" v-if="numVisibleDatas < areasData.length">
    <h3>Áreas sin datos</h3>
    <p>Hay áreas sin suficientes datos para enseñarte sus estadísticas. Continúa
    cubriendo cuestionarios para poder verlas.</p>
  </div>
  <RouterLink to="/" class="button">OK</RouterLink>
</template>

<style scoped>
h2, p {
  text-align: center;
}

.areaData, .areadata-chart {
  width: calc(100% - 20px);
  max-width: 800px;
}

.summary-chart {
  min-height: 250px;
}

.summary-chart.all {
  min-height: 350px;
}

.no-data-messages {
  text-align: center;
}
</style>
